import React, { useRef, useEffect, useState } from "react";
import { HiArrowLongDown } from "react-icons/hi2";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useMotionValue,
  useAnimation,
  useInView,
} from "framer-motion";

const Hero = ({ onScrollClick }) => {
  const ref = useRef(null);
  const buttonRef = useRef(null);
  const isButtonInView = useInView(buttonRef, { once: true });
  const controls = useAnimation();
  const [bgAnimationComplete, setBgAnimationComplete] = useState(false);
  const [buttonControls] = useState(useAnimation());
  const [buttonAnimationComplete, setButtonAnimationComplete] = useState(false);
  const [isButtonHovering, setButtonHovering] = useState(false);
  const [isButtonAnimating, setButtonAnimating] = useState(false);

  const buttonY = useMotionValue("100%");

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
    layoutEffect: false,
  });

  const bgScale = useMotionValue(10);
  const dynamicBgScale = useTransform(scrollYProgress, [0, 1], [1.1, 10]);
  const smoothBgScale = useSpring(bgScale, { stiffness: 100, damping: 30 });

  const bgX = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);
  const smoothBgX = useSpring(bgX, { stiffness: 100, damping: 30 });

  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const smoothBgY = useSpring(bgY, { stiffness: 100, damping: 30 });

  useEffect(() => {
    controls
      .start({
        scale: 1.5,
        transition: { duration: 1.5, ease: "easeInOut" },
      })
      .then(() => {
        setBgAnimationComplete(true);
        bgScale.set(dynamicBgScale.get());
      });
  }, [controls, bgScale, dynamicBgScale]);

  useEffect(() => {
    if (bgAnimationComplete) {
      const unsubscribe = dynamicBgScale.on("change", (value) => {
        bgScale.set(value);
      });
      return () => unsubscribe();
    }
  }, [bgAnimationComplete, dynamicBgScale, bgScale]);

  useEffect(() => {
    if (!isButtonInView) return;

    buttonControls
      .start({
        y: ["100%", "0%", "100%"],
        transition: {
          duration: 2,
          times: [0, 0.5, 1],
          delay: 7,
          ease: "easeInOut",
        },
      })
      .then(() => {
        setButtonAnimationComplete(true);
        buttonY.set("100%");
      });
  }, [buttonControls, buttonY, isButtonInView]);

  const handleButtonHoverStart = async () => {
    if (buttonAnimationComplete && !isButtonHovering && !isButtonAnimating) {
      setButtonAnimating(true);
      setButtonHovering(true);

      await Promise.all([
        buttonControls.start({
          y: "0%",
          transition: {
            duration: 0.5,
            times: [0, 1],
            ease: "easeInOut",
          },
        }),
      ]);

      setButtonAnimating(false);
    }
  };

  const handleButtonHoverEnd = async () => {
    if (!buttonAnimationComplete) return;

    buttonControls.stop();

    setButtonAnimating(true);
    setButtonHovering(false);

    await Promise.all([
      buttonControls.start({
        y: "100%",
        transition: {
          duration: 0.5,
          times: [0, 1],
          ease: "easeInOut",
        },
      }),
    ]);

    setButtonAnimating(false);
  };

  return (
    <div ref={ref} className="relative flex flex-col md:flex-row justify-center md:justify-between items-center h-screen w-full text-white px-5 sm:px-10 lg:px-16 py-24 gap-10 overflow-hidden">
      <motion.div
        className="absolute inset-0 bg-no-repeat bg-cover bg-center"
        style={{
          backgroundImage: "url('/image/bg-hero-arah-kebijakan.webp')",
          scale: smoothBgScale,
          x: smoothBgX,
          y: smoothBgY,
        }}
        initial={{ scale: 10 }}
        animate={controls}
      />
      <motion.div
        className="absolute inset-0"
        initial={{
          opacity: 1,
          backgroundColor: "#000000",
        }}
        whileInView={{
          opacity: 0.5,
          backgroundColor: "#2C2A2A",
        }}
        transition={{
          duration: 1,
        }}
        viewport={{
          once: true,
        }}
      />
      <div className="flex flex-col justify-between items-end w-full md:w-1/2 h-auto md:h-full gap-16 md:gap-0 z-10">
        <div />
        <div className="flex flex-col w-full gap-8">
          <motion.div
            className="text-left text-6xl md:text-8xl font-bold"
            initial={{
              opacity: 0,
            }}
            whileInView={{
              opacity: 1,
            }}
            transition={{
              duration: 1,
              delay: 2.5,
            }}
            viewport={{
              once: true,
            }}
          >
            Arah Kebijakan
          </motion.div>
          <motion.div
            className="text-3xl sm:text-4xl md:text-6xl font-light leading-tight"
            initial={{
              opacity: 0,
            }}
            whileInView={{
              opacity: 1,
            }}
            transition={{
              duration: 1,
              delay: 3.5,
            }}
            viewport={{
              once: true,
            }}
          >
            Pengembangan UMKM & <br /> Kewirausahaan
          </motion.div>
        </div>
        <div className="w-full text-4xl md:text-6xl font-bold">
          <motion.span
            initial={{
              opacity: 0,
              y: 50,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.5,
              delay: 4.5,
            }}
            viewport={{
              once: true,
            }}
          >
            2025
          </motion.span>
          <motion.span
            initial={{
              opacity: 0,
            }}
            whileInView={{
              opacity: 1,
            }}
            transition={{
              duration: 0.5,
              delay: 5,
            }}
            viewport={{
              once: true,
            }}
          >
            -
          </motion.span>
          <motion.span
            initial={{
              opacity: 0,
              x: 50,
            }}
            whileInView={{
              opacity: 1,
              x: 0,
            }}
            transition={{
              duration: 0.5,
              delay: 5.5,
            }}
            viewport={{
              once: true,
            }}
          >
            2029
          </motion.span>
        </div>
      </div>
      <div className="flex justify-end items-end w-full md:w-1/2 h-auto md:h-full z-10">
        <motion.button
          ref={buttonRef}
          className="relative group cursor-pointer flex px-3 py-5 rounded-2xl border-[3px] border-white bg-transparent overflow-hidden"
          onClick={onScrollClick}
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{
            duration: 1,
            delay: 6,
          }}
          onMouseEnter={handleButtonHoverStart}
          onMouseLeave={handleButtonHoverEnd}
          aria-label="scroll down"
        >
          <motion.div
            className="absolute inset-0 bg-white w-full h-full z-0"
            animate={buttonControls}
          />
          <HiArrowLongDown
            size={50}
            className="text-white mix-blend-difference pointer-events-none transition-all duration-500"
          />
        </motion.button>
      </div>
    </div>
  );
};

export default Hero;
